import { useState, useEffect } from "react";
import { X, Expand } from "lucide-react";
import { Eyebrow, SectionHeading } from "../common/Type";
import { Reveal, RevealStagger, RevealItem } from "../common/Reveal";

const specs = [
  {
    spec: "Biometric access",
    value: "Fingerprint sign-in",
    body: "A physical fingerprint sensor on top of the unit gates access to the appliance itself — on-device authentication, not a password typed into a shared browser.",
  },
  {
    spec: "Memory",
    value: "32GB RAM",
    body: "Room to run your configured skills, integrations, and automations at the same time — without them competing for working memory.",
  },
  {
    spec: "Storage",
    value: "1TB SSD",
    body: "Fast solid-state storage for your approved documents, knowledge, and local data, so the agent reaches what it needs without waiting on a disk.",
  },
  {
    spec: "Networking",
    value: "2.5GbE",
    body: "2.5 Gigabit wired Ethernet for a stable, high-headroom connection — steady CRM syncs, voice sessions, and integrations across your network.",
  },
];

const why = [
  "A mini-PC footprint that sits beside a monitor — not a tower under the desk.",
  "Quiet, office-appropriate operation, designed to live in a working environment.",
  "A low-power dedicated system rather than a full workstation left running all day.",
  "Always-on and committed to your agent, so workflows keep running in the background.",
  "Fast local storage and 2.5GbE networking, so integrations and voice workflows stay responsive.",
];

export default function AgentHardware() {
  const [zoom, setZoom] = useState(null);
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => { if (e.key === "Escape") setZoom(null); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const lenis = window.__lenis;
    if (lenis && lenis.stop) lenis.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      if (lenis && lenis.start) lenis.start();
    };
  }, [zoom]);
  return (
    <section className="relative py-24 md:py-32" data-testid="agent-hardware">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <Eyebrow className="mb-5">The appliance</Eyebrow>
          <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl font-bold uppercase tracking-tight leading-[0.95] text-kv-off" data-testid="hardware-product-title">
            Mythoros©
          </h2>
          <SectionHeading className="mt-4">Purpose-built hardware, sized for the desk.</SectionHeading>
          <p className="mt-6 font-body text-base md:text-lg text-kv-grey leading-relaxed">
            Mythoros© runs on a compact, dedicated appliance — not a shared laptop, and not a tab in
            someone's browser. It stays on, does one job, and keeps your business systems running quietly in the background.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <Reveal className="lg:col-span-6">
            <button
              type="button"
              onClick={() => setZoom({ src: "/assets/device/product-hand.png", alt: "Mythoros© — fingerprint sign-in on top and rear input/output" })}
              className="group relative block w-full kv-card overflow-hidden h-full p-3 flex items-center cursor-zoom-in text-left"
              data-testid="hardware-image-fingerprint"
              aria-label="Enlarge image"
            >
              <img
                src="/assets/device/product-hand.png"
                alt="Mythoros© — fingerprint sign-in on top and rear input/output"
                className="w-full h-auto object-contain"
              />
              <span className="absolute right-4 top-4 flex items-center gap-1.5 bg-kv-off/85 text-white font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Expand className="h-3 w-3" /> Enlarge
              </span>
            </button>
          </Reveal>

          <RevealStagger className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {specs.map((s) => (
              <RevealItem key={s.value} className="kv-card p-6 md:p-7" data-testid={`hardware-spec-${s.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-steel mb-3">{s.spec}</div>
                <div className="font-display text-2xl md:text-3xl font-medium tracking-tight text-kv-off">{s.value}</div>
                <p className="mt-3 font-body text-sm text-kv-grey leading-relaxed">{s.body}</p>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>

        {/* Norton Antivirus — device security. LOGO PLACEHOLDER: drop the supplied file at
            /assets/device/norton-logo.png and it will appear automatically (alt already set). */}
        <Reveal>
          <div className="kv-card p-6 md:p-8 mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="hardware-norton">
            <div
              className="relative shrink-0 h-16 w-40 rounded-[8px] bg-white border border-kv-steel/15 flex items-center justify-center overflow-hidden p-2.5"
              data-testid="norton-logo-placeholder"
            >
              <img
                src="/assets/device/norton-logo.png"
                alt="Norton Antivirus"
                className="relative z-10 max-h-11 max-w-[140px] w-auto h-auto object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-steel mb-2">Device security</div>
              <div className="font-display text-2xl md:text-3xl font-medium tracking-tight text-kv-off">Norton Antivirus</div>
              <div className="mt-2 font-display text-base md:text-lg font-medium text-kv-off">Norton Business — Triple-lock cybersecurity for your team.</div>
              <p className="mt-3 font-body text-sm md:text-base text-kv-grey leading-relaxed max-w-2xl">
                Every Mythoros© appliance ships with Norton Antivirus pre-installed, providing enterprise-grade protection against malware and threats from day one.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          <Reveal className="lg:col-span-7">
            <button
              type="button"
              onClick={() => setZoom({ src: "/assets/device/product-views.png", alt: "Mythoros© — front ports, top panel, rear I/O with cooling, and profile view" })}
              className="group relative block w-full kv-card overflow-hidden p-3 cursor-zoom-in text-left"
              data-testid="hardware-image-views"
              aria-label="Enlarge image"
            >
              <img
                src="/assets/device/product-views.png"
                alt="Mythoros© — front ports, top panel, rear I/O with cooling, and profile view"
                className="w-full h-auto object-contain"
              />
              <span className="absolute right-4 top-4 flex items-center gap-1.5 bg-kv-off/85 text-white font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Expand className="h-3 w-3" /> Enlarge
              </span>
            </button>
          </Reveal>

          <Reveal className="lg:col-span-5">
            <div className="kv-card p-8 md:p-9 h-full">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-steel mb-6">Why a dedicated appliance</div>
              <ul className="space-y-4">
                {why.map((w) => (
                  <li key={w} className="flex items-start gap-3">
                    <span className="mt-2 h-1 w-4 bg-kv-electric/80 shrink-0" aria-hidden />
                    <span className="font-body text-sm md:text-base text-kv-off leading-relaxed">{w}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-7 font-body text-sm text-kv-steel leading-relaxed">
                Configuration is scoped to your business. Final hardware options are confirmed during your consultation.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={zoom.alt}
          data-testid="image-lightbox"
          onClick={() => setZoom(null)}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm animate-in fade-in-0" />
          <button
            type="button"
            onClick={() => setZoom(null)}
            aria-label="Close"
            data-testid="image-lightbox-close"
            className="absolute right-5 top-5 z-10 text-white/70 hover:text-white transition-colors"
          >
            <X className="h-7 w-7" />
          </button>
          <img
            src={zoom.src}
            alt={zoom.alt}
            className="relative z-10 max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain rounded-[10px] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.7)] animate-in fade-in-0 zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
